import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { isOfflineQueuedError } from '../../../../offline/isOfflineQueuedError';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Sentry
import * as Sentry from '@sentry/react';
import debug from 'debug';

// Components
import { MainButton } from '../../../inputs/buttons/MainButton';
import ModalContainer from '../../containers/ModalContainer';
import { AlertIcon } from '../../../../assets/icons/AlertIcon';
import { Typography } from '../../../display/Typography';

// Styled Components
import {
  Container,
  ContainerText,
  Title,
  Text,
  ContainerInputSection,
  MainButtonStyled,
  ContainerForm,
  WarningButtonRow,
  WarningButton,
} from './styles';

// Types
import { ModalProps } from './types';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { Controller, useForm } from 'react-hook-form';
import { OTObject } from '../../../display/ShowOT/WorkOrder/types';
import { client } from '../../../../gql/client';
import {
  evictDerivedCaches,
  syncOrderStateToTwinEntityCache,
} from '../../../../gql/cacheHelpers';

// GQL
import { UPDATE_ORDER } from '../../../../gql/mutations/orders';
import { OrderState, OrderType } from '../../../../types/orders';
// Auth & Permissions
import { useAuth } from '../../../../hooks/useAuth';
import { Role } from '../../../../types/auth';
import {
  getAvailableStateTransitions,
  getSrUndoTarget,
} from '../../../../utils/rolePermissions';

// Schema
import { OTModalSchema } from './schema';
import { useCacheFormInSessionStorage } from '../../../../hooks/useCacheForm';

// Constants
import { OTModalKey } from './constants';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';
import {
  decideCloseNoteAfterStateChange,
  isStateChangeCloseTrigger,
} from '../../../../utils/closeOrderPrompt';
import {
  showSuccessToast,
  showErrorToast,
  showBackendErrorToast,
} from '../../../../utils/toasts';
import {
  VALIDATE_SURVEILLANCE_ROUTE,
  ANNUL_SURVEILLANCE_ROUTE,
  REOPEN_SURVEILLANCE_ROUTE,
  FINISH_SURVEILLANCE_ROUTE,
} from '../../../../gql/mutations/surveillanceRoute';
// NOTE: el modal ya NO escribe registerEndDate. Cambiar de estado solo manda
// { id, state }; la fecha-fin se edita exclusivamente desde el form de la
// entidad. Esto evita la corrupción de la hora-fin (ventana de horas) al
// finalizar/validar — ver memoria fecha-fin-corrupta-actuaciones.
import { FETCH_ORDERS } from '../../../../gql/queries/orders/orders';
import { GET_ACTIVE_ROUTE_SEGMENT } from '../../../../gql/queries/surveillanceRoute';
import { GET_ACTION_EXPEDITION_CLOSE_INFO } from '../../../../gql/queries/actions/actions';
import { OptionSelected } from '../../../inputs/DropDown/DropDownList/types';

// Redux: T99 dispatch tras UPDATE_ORDER de Action a Validated/PendingCert
import { useDispatch } from 'react-redux';
import {
  orderCloseSetPending,
  orderCloseRemoveDeclined,
} from '../../../../state/actions/ui/orderCloseConfirmation';
import { openModal, closeModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../SuperModal/types';
import { useSurveillanceRoutes } from '../../../../hooks/useSurveillanceRoutes';
import { PauseTimerConfirmPayload } from '../PauseSurveillanceRouteModal/types';

/**
 * Detecta si un error proviene de OfflineQueueLink (mutation encolada para
 * sync futura). Mismo helper usado en saveDatas Bloque C y en los `action.ts`
 * de los routes (C-11.9).
 */

export const ChangeStateModal = ({
  onClickClose,
  title,
  titleSaveModal,
  indexExpedition,
  revalidatorFunction,
  orderType,
  currentState,
  orderId,
  communicationId,
  routeId,
  roadId,
  // Se sacan del resto: `props` acaba en un <div> y estos no son del DOM.
  segmentReviewWarning,
  onReviewRemaining,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation('modals');
  const theme = useTheme();
  const { currentRole } = useAuth();
  const dispatch = useDispatch();
  // Cierre de tramo + entrega encadenados (RV): mismo par de mutations que
  // usa el botón «Finalizar» de la cabecera.
  const { finishSegment, finishRoute } = useSurveillanceRoutes();
  const cacheData = sessionStorage.getItem(OTModalKey);
  const initialData = cacheData
    ? JSON.parse(decompressSessionStorage(cacheData))
    : {};
  const {
    handleSubmit,
    control,
    formState: { errors, defaultValues },
    getValues,
  } = useForm<OTObject>({
    resolver: yupResolver(OTModalSchema) as any,
    defaultValues: { ...initialData },
  });

  useCacheFormInSessionStorage(OTModalKey, control);

  const [initialModal, setInitialModal] = useState<boolean>(true);
  // «Tramos revisados» (D7): paso intermedio entre elegir «Validado» y confirmar.
  const [showReviewWarning, setShowReviewWarning] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [textError, setTextError] = useState('');
  const [orders, setOrders] = useState<OTObject[]>([]);

  // Debug loggers
  const logMain = debug('rois:state-change:main');
  const logValidation = debug('rois:state-change:validation');
  const logUpdate = debug('rois:state-change:update');
  const logCache = debug('rois:state-change:cache');

  // Enable logs only in development
  if (import.meta.env.DEV) {
    // You can use localStorage to activate all logs in development
    debug.enable('rois:state-change:*');

    // Initialization messages
    logMain('Debug activado para módulo de state-change');
  }

  /**
   * Get all available states filtered by user role
   * Only shows states that the current user role has permission to use
   */
  /**
   * Destinos que este rol puede dar a esta entidad desde donde está hoy.
   *
   * El cálculo vive ENTERO en `getAvailableStateTransitions()`
   * (`utils/rolePermissions.ts`), que es el mismo que usan los sitios que
   * deciden si pintar el gesto. Antes estaba aquí dentro, en dos `useMemo`
   * encadenados, y uno de sus filtros (la política de destinos manuales de una
   * Actuación) no existía en ninguna otra capa: ni en el espejo, ni en el
   * backend, ni en el tripwire. Esa regla huérfana es la que dejaba el
   * desplegable vacío sin que nadie se enterara.
   *
   * Aquí solo queda poner etiqueta a cada estado y conservar el ORDEN de
   * presentación (el del array, no el del enum).
   */
  const options = useMemo(() => {
    const available = getAvailableStateTransitions(
      currentRole as Role,
      orderType,
      currentState as OrderState
    );

    const allStates = [
      { label: t('changeState.states.open'), value: OrderState.Open },
      { label: t('changeState.states.managed'), value: OrderState.Managed },
      {
        label: t('changeState.states.pending-work'),
        value: OrderState.PendingWork,
      },
      {
        label: t('changeState.states.work-in-progress'),
        value: OrderState.WorkInProgress,
      },
      // V4 (2026-04-21) D5: estados específicos del RV.
      // 2026-07-20: labels canónicos de common:states (keys aprobadas) — el
      // namespace modals no tiene estos dos estados (propios del RV).
      {
        label: t('common:states.in-progress'),
        value: OrderState.InProgress,
      },
      {
        label: t('common:states.paused'),
        value: OrderState.Paused,
      },
      {
        label: t('changeState.states.pending-validation'),
        value: OrderState.PendingValidation,
      },
      {
        label: t('changeState.states.pending-at-validation'),
        value: OrderState.PendingATValidation,
      }, // T66
      {
        label: t('changeState.states.validated'),
        value: OrderState.Validated,
      },
      {
        label: t('changeState.states.pending-certification'),
        value: OrderState.PendingCertification,
      },
      { label: t('changeState.states.annulled'), value: OrderState.Annulled },
      { label: t('changeState.states.end'), value: OrderState.End },
    ];

    const finalOptions = allStates.filter((option) =>
      available.includes(option.value)
    );

    logMain('Destinos disponibles:', {
      role: currentRole,
      orderType,
      currentState,
      allowedValues: finalOptions.map((opt) => opt.value),
    });

    return finalOptions;
  }, [t, currentRole, orderType, currentState]);

  const accidents = orders.filter((order) => order.type === OrderType.Accident);
  const communications = orders.filter(
    (order) => order.type === OrderType.Communication
  );

  /**
   * `FilterOrderList.indexExpedition` es un **Int** en el schema, pero la prop
   * llega como `string` en unos callers y como `number` en otros (el payload de
   * `openModal` no está tipado, así que TypeScript no lo cazaba). La cabecera
   * del RV mandaba `String(route.indexExpedition)` → la query se caía con
   * «Int cannot represent non-integer value» → el `catch` de abajo se la comía
   * → `orders` quedaba vacío → el guard de cierre creía que no había nada
   * pendiente y no avisaba. Cazado en navegador el 2026-08-05: el E2E no lo
   * vio porque el mock responde por operationName sin mirar las variables.
   *
   * Se normaliza AQUÍ (un solo sitio) en vez de fiarlo a cada caller.
   */
  const getOrders = async () => {
    const expedition = Number(indexExpedition);
    if (!Number.isFinite(expedition)) {
      logMain('indexExpedition no numérico, no se piden las hermanas:', indexExpedition);
      return null;
    }
    const { data } = await client.query({
      query: FETCH_ORDERS,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          indexExpedition: expedition,
        },
      },
    });
    return data;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // RV (2026-08-05): el recorrido TAMBIÉN necesita las hermanas. Desde
        // que existe el guard de cierre del backend
        // (`assertBasicActionsReadyForValidation`), validar un recorrido con
        // básicas sin revisar se rechaza — pero sin estos datos el modal no
        // podía avisar ANTES y el usuario se enteraba por un toast después de
        // pulsar Continuar, mientras que en la OT genérica el aviso sale
        // dentro del modal. Misma regla, mismo momento.
        //
        // La reserva original (la visibilidad por rol podía devolver vacío
        // para Tablet) no aplica al caso que importa: solo Coex/Capataz/
        // SuperAdmin pueden llevar un recorrido a `end`. Y si la lista llegara
        // vacía se falla ABIERTO — el backend sigue siendo la barrera real.
        const dataOrders = await getOrders();
        if (dataOrders) setOrders(dataOrders.orders.orders);
      } catch (err) {
        Sentry.captureException(err);
        logMain(err);
      }
    };
    fetchData();
  }, [indexExpedition]); // FIX: Changed from [orders] to [indexExpedition] to prevent infinite loop

  /**
   * Counts and categorizes actions in the current expedition
   * Returns action statistics for validation purposes
   */
  const getActionStats = useCallback(() => {
    let actions = 0;
    let validatedActions = 0;
    let openOrPartialActions = 0;
    let nonValidatedActions = 0;
    let annulledActions = 0;
    let accidents = 0;

    logValidation('Checking order states in expedition:');
    orders.forEach((order) => {
      if (order.type === OrderType.Action) {
        actions += 1;
        logValidation(`Found Action order: ${order.id}, state: ${order.state}`);
        if (
          order.state === OrderState.Validated ||
          order.state === OrderState.PendingCertification ||
          // 2026-06-10 (main) — una actuación ya FINALIZADA (`end`) cuenta
          // como terminada para cerrar la OT padre, igual que la autoridad
          // del backend (validateActionsForClosure / checkAllActionsValidated,
          // que aceptan `end`).
          order.state === OrderState.End
        ) {
          validatedActions += 1;
        } else if (
          order.state === OrderState.Open ||
          order.state === OrderState.Partial
        ) {
          openOrPartialActions += 1;
          logValidation(
            `Action with open/partial state detected: ${order.id}, state: ${order.state}`
          );
        } else if (order.state === OrderState.Annulled) {
          // Las actuaciones anuladas se IGNORAN para cerrar la OT padre, igual
          // que la autoridad del backend (validateActionsForClosure /
          // checkAllActionsValidated, que excluyen Annulled). Si no, una
          // actuación anulada bloqueaba erróneamente el cierre con
          // "actuaciones sin revisar". Contador informativo, NO bloquea.
          // Ver .claude/rules/rbac-architecture.md (sincronización front↔back).
          annulledActions += 1;
          logValidation(
            `Action annulled — ignored for closure: ${order.id}, state: ${order.state}`
          );
        } else {
          // Actions that are not validated and not open/partial
          nonValidatedActions += 1;
          logValidation(
            `Action with non-validated state detected: ${order.id}, state: ${order.state}`
          );
        }
      } else if (order.type === OrderType.Accident) {
        accidents += 1;
        logValidation(
          `Found Accident order: ${order.id}, state: ${order.state}`
        );
      }
    });

    const stats = {
      actions,
      validatedActions,
      openOrPartialActions,
      nonValidatedActions,
      annulledActions,
      accidents,
    };

    logValidation('Order counts summary:', {
      totalOrders: orders.length,
      ...stats,
    });

    return stats;
  }, [orders]);

  /**
   * Validates state transition for Generic and Campaign orders
   * Different validations apply based on target state
   */
  const validateGenericOrderStateChange = useCallback(
    (
      targetState: OrderState,
      stats: ReturnType<typeof getActionStats>
    ): boolean => {
      const { actions, openOrPartialActions, nonValidatedActions } = stats;

      // ✅ VALIDATION 1: Cannot close empty generic orders
      if (targetState === OrderState.End && orders.length === 1) {
        logValidation(
          'Validation failed: Cannot close generic orders without associated items'
        );
        setTextError(t('changeState.errors.cannotCloseEmpty'));
        return false;
      }

      // ✅ VALIDATION 2: When closing (End), all actions must be validated
      if (targetState === OrderState.End) {
        if (actions === 0) {
          logValidation(
            'Validation passed: No actions to validate when closing'
          );
          return true;
        }

        // Check if there are any actions that are open, partial, or not validated
        const totalNonValidated = openOrPartialActions + nonValidatedActions;
        if (totalNonValidated > 0) {
          logValidation(
            'Validation failed: Cannot close with non-validated actions',
            {
              openOrPartialActions,
              nonValidatedActions,
              totalNonValidated,
            }
          );
          setTextError(t('changeState.errors.unvalidatedActions'));
          return false;
        }

        logValidation('Validation passed: All actions validated, can close');
        return true;
      }

      // ✅ VALIDATION 3: When annulling, no specific action validation required
      // Annulling should be allowed regardless of action states
      if (targetState === OrderState.Annulled) {
        logValidation(
          'Validation passed: Annulling order (no action validation required)'
        );
        return true;
      }

      // ✅ VALIDATION 4 (BUG #8a FIX): ELIMINADA
      // La validación de acciones open/partial era innecesaria - el backend ya maneja esto correctamente
      // con bypassWorkflow para roles administrativos y VALID_STATE_FLOW para todos los roles

      // ✅ VALIDATION 5: Allow all other intermediate state transitions
      // (open → managed, managed → pending-work, etc.)
      logValidation('Validation passed: Intermediate state transition allowed');
      return true;
    },
    [orders, t]
  );

  /**
   * Espejo del guard de cierre del backend para el Recorregut de Vigilància
   * (`assertBasicActionsReadyForValidation`), que a su vez es espejo del de la
   * OT genérica. Aquí solo se adelanta el aviso: la autoridad es el backend.
   *
   * Solo mira el destino `end` (= "Validado"/cerrar el recorrido). Entregar
   * (`pending-validation`), reabrir (`paused`) y anular NO se bloquean — igual
   * que en la genérica, y por el motivo que documenta el guard del backend: al
   * entregar es NORMAL que las básicas estén sin revisar.
   *
   * Tampoco se aplica el "no cierres una expedición vacía" de la genérica: un
   * recorrido sin ninguna básica es un turno perfectamente normal.
   */
  const validateSurveillanceRouteStateChange = useCallback(
    (
      targetState: OrderState,
      stats: ReturnType<typeof getActionStats>
    ): boolean => {
      if (targetState !== OrderState.End) return true;

      const totalNonValidated =
        stats.openOrPartialActions + stats.nonValidatedActions;
      if (totalNonValidated > 0) {
        logValidation(
          'Validation failed: RV con básicas sin revisar, no se puede validar',
          stats
        );
        setTextError(t('changeState.errors.unvalidatedActions'));
        return false;
      }
      return true;
    },
    [t]
  );

  const onSubmit = useCallback(async () => {
    const formValues = getValues();

    const validation = () => {
      logValidation('Running validation for state change', {
        currentState,
        newState: formValues?.state,
        orderType,
        ordersCount: orders.length,
      });

      // Los tres tipos CONTENEDOR (D0) cierran con la misma regla: no se cierra
      // un expediente con actuaciones sin revisar. El recorrido se sumó el
      // 2026-08-05 — la regla ya existía en su backend, aquí faltaba el aviso.
      if (
        orderType !== OrderType.Generic &&
        orderType !== OrderType.Campaign &&
        orderType !== OrderType.SurveillanceRoute
      ) {
        logValidation('Validation skipped: no es un tipo contenedor');
        return true;
      }

      // Get action statistics for validation
      const actionStats = getActionStats();

      // Validate state transition based on target state
      return orderType === OrderType.SurveillanceRoute
        ? validateSurveillanceRouteStateChange(
            formValues.state as OrderState,
            actionStats
          )
        : validateGenericOrderStateChange(
            formValues.state as OrderState,
            actionStats
          );
    };

    const isValid =
      validation() ||
      orderType === OrderType.Action ||
      orderType === OrderType.Accident ||
      orderType === OrderType.Communication;
    logValidation('Validation result:', { isValid, orderType });

    if (!isValid) {
      setValidationError(true);
      return;
    }
    // D7 (27-oct): validar un recorrido con tramos sin revisar AVISA, no
    // bloquea. Va DESPUÉS de las comprobaciones de siempre (las básicas sin
    // revisar siguen parando antes) y ANTES de «¿Confirmar cambio?».
    if (
      orderType === OrderType.SurveillanceRoute &&
      formValues?.state === OrderState.End &&
      segmentReviewWarning &&
      segmentReviewWarning.unreviewed > 0
    ) {
      setShowReviewWarning(true);
      return;
    }
    setInitialModal(!initialModal);
  }, [orders, initialModal, orderType, currentState, segmentReviewWarning]);

  /** «Continuar igualmente»: al paso de confirmación de siempre. */
  const continueDespiteReviewWarning = useCallback(() => {
    setShowReviewWarning(false);
    setInitialModal(false);
  }, []);

  /** «Revisar los que faltan»: cierra y baja al primer tramo sin revisar. */
  const reviewRemaining = useCallback(() => {
    onClickClose();
    onReviewRemaining?.();
  }, [onClickClose, onReviewRemaining]);

  const sendData = useCallback(async () => {
    const formValues = getValues();
    logMain('Starting state update', {
      orderType,
      orderId,
      newState: formValues?.state,
    });

    // ── RV (2026-07-20): la máquina del RV NO se pilota con UPDATE_ORDER
    // (validador genérico ≠ matriz D5; sin cascada a tramos). Cada estado
    // destino mapea a su mutation SR — el backend valida rol+transición
    // (2ª barrera) y cascadea a tramos dentro de su $transaction. ──
    if (orderType === OrderType.SurveillanceRoute) {
      const target = formValues?.state as OrderState;
      if (!routeId) {
        logMain('SR state change sin routeId — abortando');
        showErrorToast(t('changeState.errors.generic'));
        return;
      }
      // Enrutar por ORIGEN antes que por destino (2026-08-05). `end →
      // pending-validation` (des-validar) tiene el MISMO destino que entregar,
      // así que un mapa por destino lo mandaría a cerrar tramos y a
      // `finishSurveillanceRoute` — sobre un recorrido ya validado. Las tres
      // vueltas atrás son la misma mutation.
      const undoTarget = getSrUndoTarget(currentState as OrderState);
      const isUndo = undoTarget !== null && undoTarget === target;

      try {
        if (target === OrderState.PendingValidation && !isUndo) {
          // Entregar (Tablet dueño / SuperAdmin rescate).
          //
          // 2026-08-05: si queda un tramo ABIERTO hay que cerrarlo ANTES, con
          // su PK final, su causa y su carril. Hasta hoy esta rama llamaba a
          // la mutation a pelo y el backend cerraba el tramo en cascada con
          // SOLO `endDate`: ese tramo dejaba de sumar kilómetros, el mapa no
          // lo dibujaba y desaparecía del «Resumen por causa» del informe.
          //
          // El botón «Finalizar» de la cabecera siempre lo hizo bien (abre el
          // modal y encadena finishSegment → finishRoute). Aquí se hace lo
          // mismo: se cede el turno a ese modal y la entrega ocurre DENTRO de
          // su confirmación. El backend además lo exige (SR_SEGMENT_STILL_OPEN),
          // así que esto no es la única barrera — es la que evita el error.
          const { data: activeData } = await client.query({
            query: GET_ACTIVE_ROUTE_SEGMENT,
            variables: { surveillanceRouteId: routeId },
            fetchPolicy: 'network-only',
          });
          const openSegment = activeData?.activeRouteSegment;

          if (openSegment?.id) {
            dispatch(
              openModal(modalIds.PAUSE_SURVEILLANCE_ROUTE, {
                roadId,
                onConfirm: async (payload: PauseTimerConfirmPayload) => {
                  try {
                    // Mismo encadenado que `useRouteTimerActions`: un solo
                    // toast y ambos pasos encolables si no hay cobertura.
                    await finishSegment(
                      {
                        id: openSegment.id,
                        endDate: new Date().toISOString(),
                        pkEndKm: payload.pkEndKm,
                        pkEndMeter: payload.pkEndMeter,
                        pauseCause: payload.pauseCause,
                        pauseCauseDetail: payload.pauseCauseDetail,
                        lane: payload.lane,
                      },
                      { silentSuccess: true }
                    );
                    await finishRoute(
                      { id: routeId, registerEndDate: new Date().toISOString() },
                      { silentSuccess: true }
                    );
                    showSuccessToast(t('toasts:success.updated'));
                    dispatch(closeModal());
                    revalidatorFunction && revalidatorFunction();
                  } catch (err) {
                    Sentry.captureException(err);
                    showBackendErrorToast(err, t('changeState.errors.generic'));
                  }
                },
              })
            );
            return;
          }

          await client.mutate({
            mutation: FINISH_SURVEILLANCE_ROUTE,
            variables: { input: { id: routeId } },
          });
        } else {
          const srMutationByTarget: Partial<
            Record<OrderState, typeof VALIDATE_SURVEILLANCE_ROUTE>
          > = {
            [OrderState.End]: VALIDATE_SURVEILLANCE_ROUTE,
            [OrderState.Annulled]: ANNUL_SURVEILLANCE_ROUTE,
            // Reabrir/devolver: pending-validation → paused (matriz 2026-07-20).
            [OrderState.Paused]: REOPEN_SURVEILLANCE_ROUTE,
          };
          // Deshacer gana al mapa por destino: des-validar y des-anular son
          // `reopen`, aunque sus destinos coincidan con los de otras ramas.
          const srMutation = isUndo
            ? REOPEN_SURVEILLANCE_ROUTE
            : srMutationByTarget[target];
          if (!srMutation) {
            logMain('SR transición sin mutation asociada:', target);
            showErrorToast(t('changeState.errors.invalidTransition'));
            return;
          }
          await client.mutate({
            mutation: srMutation,
            variables: { id: routeId },
          });
        }
        evictDerivedCaches(client.cache);
        sessionStorage.removeItem(OTModalKey);
        showSuccessToast(t('changeState.successMessage'));
        revalidatorFunction && revalidatorFunction();
        onClickClose();
      } catch (err) {
        if (isOfflineQueuedError(err)) {
          sessionStorage.removeItem(OTModalKey);
          showSuccessToast(t('changeState.queuedOffline'));
          revalidatorFunction && revalidatorFunction();
          onClickClose();
          return;
        }
        Sentry.captureException(err);
        // Escalera SR: código SR_* → errors:backend.<code> traducido;
        // business 4xx sin código → mensaje crudo; técnico → genérico.
        showBackendErrorToast(err, t('changeState.errors.generic'));
        setInitialModal(true);
      }
      return;
    }

    try {
      // Update main order
      logUpdate('Updating main order:', orderId);
      const { data: updatedOrderData } = await client.mutate({
        mutation: UPDATE_ORDER,
        variables: {
          input: {
            id: orderId,
            state: formValues.state,
          },
        },
      });

      const updatedOrder = updatedOrderData?.updateOrder;
      const actualOrderType = updatedOrder?.type || orderType;

      logUpdate('Main order updated:', {
        orderId,
        updatedOrderData,
        actualOrderType,
      });

      // The backend's syncStateToRelatedEntities() automatically syncs the
      // Order state to its horizontal entity (GenericOrder, Communication,
      // Action, Accident) when UPDATE_ORDER is called. No need to update
      // the entity separately from the frontend — doing so was redundant
      // and created desync risk if the second mutation failed.
      logUpdate('Order updated — backend handles entity sync automatically');

      // Pero esa sincronía ocurre en SERVIDOR — la respuesta de UPDATE_ORDER
      // solo trae la Order, así que la caché del CLIENTE deja la entidad
      // gemela (Communication/Accident/Action/GenericOrder/Campaign) con el
      // estado viejo hasta el próximo fetch de red. Espejarlo aquí evita que
      // un consumidor que lea el estado de la ENTIDAD (el resumen de
      // Comunicación lo hace) sirva dato rancio sin salir de la página.
      syncOrderStateToTwinEntityCache(client.cache, updatedOrder);

      // El backend ya cascadeó a las hijas (Communication/Accident) dentro de
      // su propia transacción (`cascadeExpeditionChildren`) — ya no hace
      // falta este bucle de N mutaciones UPDATE_ORDER secuenciales desde el
      // cliente. Ese bucle vivía en un único `try` sin `catch` por
      // iteración: el primer fallo abortaba el resto y dejaba el expediente
      // a medio cerrar (incidente real: de 55 comunicaciones se cerraron 8 y
      // quedaron 47 abiertas bajo una OT ya cerrada).
      //
      // En vez de pedir N veces GET_ORDER_BY_ID por red (eran 55 peticiones
      // en la OT 2421) para refrescar lo que el backend ya escribió, se
      // evictan las hijas de la caché de Apollo y se repueblan cuando
      // alguien las mire.
      //
      // Se evictan las DOS mitades de cada entidad doble: la Order y su
      // gemela (Communication/Accident). Si solo se evicta la Order, el
      // resumen de comunicación —que pinta `communication.state ||
      // order.state`, o sea el de la ENTIDAD— sigue sirviendo el estado
      // viejo (ya nos pasó, PR #198).
      //
      // Misma puerta que tenía el bucle borrado: el backend solo cascadea
      // con padre Generic/Campaign y destino end|annulled|open. Sin esta
      // puerta el evict corría SIEMPRE — validar una actuación (el gesto
      // más frecuente de la app) evictaba hasta 12 comunicaciones/
      // accidentes hermanas (y sus gemelas) que el servidor no había
      // tocado. No rompe corrección (se repueblan al leerlas), pero en una
      // app OFFLINE-FIRST `evict` DESTRUYE el dato en vez de refrescarlo:
      // una tablet que se queda sin cobertura justo después pierde la
      // lectura de las hermanas que el código viejo sí conservaba.
      //
      // De regalo cierra el auto-evict: al cambiar el estado de una
      // comunicación SUELTA, `actualOrderType` es `Communication` — nunca
      // Generic/Campaign — así que esta puerta impide que se evicte a sí
      // misma justo después de haberla sincronizado arriba con
      // `syncOrderStateToTwinEntityCache`.
      const cascadeTargetState = formValues?.state as OrderState | undefined;
      const cascades =
        (actualOrderType === OrderType.Generic ||
          actualOrderType === OrderType.Campaign) &&
        (cascadeTargetState === OrderState.End ||
          cascadeTargetState === OrderState.Annulled ||
          cascadeTargetState === OrderState.Open);

      if (cascades) {
        for (const child of [...accidents, ...communications]) {
          if (!child.id) continue;
          client.cache.evict({ id: `Order:${child.id}` });
          const isCommunication = child.type === OrderType.Communication;
          const twinTypename = isCommunication ? 'Communication' : 'Accident';
          const twinIds = isCommunication
            ? child.communicationIDs
            : child.accidentIDs;
          twinIds?.forEach((twinId) => {
            client.cache.evict({ id: `${twinTypename}:${twinId}` });
          });
        }
        logCache('Child Order/entity cache entries evicted', {
          accidents: accidents.length,
          communications: communications.length,
        });
      } else {
        logCache('Cascade skip — parent/destino fuera de la puerta backend', {
          actualOrderType,
          cascadeTargetState,
        });
      }

      // Invalida informe Gestión Horaria + listas (incl. laborHoursReport) para
      // que refresquen en el siguiente loader run sin pulsar "Actualizar".
      evictDerivedCaches(client.cache);
      logCache('Table query caches evicted');

      sessionStorage.removeItem(OTModalKey);

      logMain('State update complete - closing modal');
      showSuccessToast(t('changeState.successMessage'));

      // T99: si esta UPDATE_ORDER cambió un Order tipo Action a Validated o
      // PendingCertification, comprobar si todas las actions del expediente
      // están "listas" → mostrar modal "¿Cerrar OT?". Reutiliza la lógica
      // backend `checkAllActionsValidated` (Tarea #7) vía la query GraphQL
      // `actionExpeditionCloseInfo`.
      //
      // Antes este flujo no disparaba el modal porque UPDATE_ORDER devuelve
      // `Order!` sin los flags T99 que sí trae SAVE_ACTION inline. Resultado:
      // al validar/marcar pending-cert via ChangeStateModal, ni desde el
      // primer ni segundo resumen aparecía el modal de cierre.
      const newState = formValues?.state as OrderState | undefined;
      // Validada, pendiente de certificar y finalizada (spec 2026-10-28 § 4).
      const isCloseCheckState = isStateChangeCloseTrigger(newState);
      const isActionOrder = orderType === OrderType.Action;
      // indexExpedition===0 es un expediente LEGÍTIMO (el primero de cada
      // tenant) y en JS es falsy — hay que comprobar presencia real, no
      // truthiness.
      if (
        isCloseCheckState &&
        isActionOrder &&
        indexExpedition !== undefined &&
        indexExpedition !== null
      ) {
        try {
          logMain(
            'T99 check — UPDATE_ORDER Action → state %s; querying actionExpeditionCloseInfo for indexExpedition %s',
            newState,
            indexExpedition
          );
          const { data: closeInfoData } = await client.query({
            query: GET_ACTION_EXPEDITION_CLOSE_INFO,
            variables: {
              indexExpedition: Number(indexExpedition),
            },
            fetchPolicy: 'network-only',
          });
          const closeInfo = closeInfoData?.actionExpeditionCloseInfo;
          logMain('T99 closeInfo:', closeInfo);
          // Cuándo se apunta el aviso y cuándo se olvida un «No» anterior:
          // `decideCloseNoteAfterStateChange` (lo explica allí). Primero se
          // borra el «No» y después se apunta: con la OT aún rechazada, el
          // reducer descartaría la nota.
          const decision = decideCloseNoteAfterStateChange(
            currentState,
            newState,
            closeInfo
          );
          if (decision.action === 'note') {
            if (decision.clearDecline) {
              dispatch(orderCloseRemoveDeclined(decision.parentOrderId));
            }
            dispatch(
              orderCloseSetPending(decision.parentOrderId, decision.parentRoid)
            );
            logMain(
              'T99 dispatched orderCloseSetPending parentOrderId=%s parentRoid=%s clearDecline=%s',
              decision.parentOrderId,
              decision.parentRoid,
              decision.clearDecline
            );
          }
        } catch (t99Err) {
          // No bloqueamos el cierre del modal si la query T99 falla — el
          // cambio de state ya está aplicado. Lo loggeamos para diagnóstico.
          logMain('T99 query failed (no bloqueante):', t99Err);
        }
      }

      revalidatorFunction && revalidatorFunction();
      onClickClose();
    } catch (err) {
      // Offline: la mutation se encoló por OfflineQueueLink. NO es fallo
      // real — Apollo replay-eará al reconectar. Tratamos como éxito:
      // cerrar el modal, toast informativo, NO reabrir el modal.
      // (El backend OrderStateSyncService cascadeará a children al replay.)
      if (isOfflineQueuedError(err)) {
        logMain('UPDATE_ORDER encolada offline — tratando como éxito');
        sessionStorage.removeItem(OTModalKey);
        showSuccessToast(t('changeState.queuedOffline'));
        revalidatorFunction && revalidatorFunction();
        onClickClose();
        return;
      }

      Sentry.captureException(err);
      const errorMessage = (err as Error)?.message;
      logMain('Error updating state:', errorMessage);

      // Parse specific backend errors and show translated messages
      if (errorMessage?.includes('Invalid state transition')) {
        showErrorToast(t('changeState.errors.invalidTransition'));
      } else if (errorMessage?.includes('does not have permission')) {
        showErrorToast(t('changeState.errors.noPermission'));
      } else {
        showErrorToast(t('changeState.errors.generic'));
      }
      // Keep modal open on error so user can try again
      setInitialModal(true);
    }
  }, [
    orderId,
    routeId,
    orderType,
    revalidatorFunction,
    onClickClose,
    orders,
    accidents,
    t,
  ]);

  return (
    <>
      {(orders.length > 0 || orderType === OrderType.SurveillanceRoute) && (
        <>
          {showReviewWarning && segmentReviewWarning ? (
            <ModalContainer
              width={50}
              fill={theme.colors.black}
              onClickClose={onClickClose}
            >
              <Container data-testid="validate-review-warning">
                <AlertIcon />
                <ContainerText>
                  <Title variant="semiBold">
                    {t('pages:surveillance.segmentReview.validateWarning.body', {
                      count: segmentReviewWarning.unreviewed,
                    })}
                  </Title>
                  {segmentReviewWarning.changed > 0 && (
                    <Text size="S" colorText="greyDark">
                      {t(
                        'pages:surveillance.segmentReview.validateWarning.changedNote',
                        { count: segmentReviewWarning.changed }
                      )}
                    </Text>
                  )}
                </ContainerText>
                <WarningButtonRow>
                  <WarningButton
                    type="button"
                    text={t(
                      'pages:surveillance.segmentReview.validateWarning.reviewRest'
                    )}
                    onClick={reviewRemaining}
                  />
                  <WarningButton
                    type="button"
                    variant="secondary"
                    text={t(
                      'pages:surveillance.segmentReview.validateWarning.continue'
                    )}
                    onClick={continueDespiteReviewWarning}
                  />
                </WarningButtonRow>
              </Container>
            </ModalContainer>
          ) : initialModal ? (
            <ModalContainer
              width={50}
              fill={theme.colors.black}
              onClickClose={onClickClose}
            >
              <ContainerForm onSubmit={handleSubmit(onSubmit)}>
                <AlertIcon />
                <ContainerText>
                  <Title variant="semiBold">
                    {orderType === OrderType.Communication
                      ? t('workOrder.changeCommunicationState')
                      : orderType === OrderType.Action
                        ? t('workOrder.changeActionState')
                        : orderType === OrderType.Accident
                          ? t('workOrder.changeAccidentState')
                          : orderType === OrderType.Campaign
                            ? t('workOrder.changeCampaignState')
                            : t('workOrder.changeOrderState')}
                  </Title>
                </ContainerText>
                <ContainerInputSection>
                  <Text size="S">{t('changeState.selectState')}</Text>
                  <Controller
                    control={control}
                    name="state"
                    render={({ field: { onChange } }) => {
                      return (
                        <DropDownInput
                          borderError={!!errors?.state}
                          inputSize="S"
                          typeDropDown="Default"
                          placeholder={t('common:labels.select')}
                          options={options}
                          defaultValue={defaultValues?.state}
                          onChangeSelected={(option) => onChange(option?.value)}
                        />
                      );
                    }}
                  />
                  {validationError && (
                    <Typography size="XS" colorText="red">
                      {textError}
                    </Typography>
                  )}
                </ContainerInputSection>
                <MainButton text={t('common:buttons.continue')} type="submit" />
              </ContainerForm>
            </ModalContainer>
          ) : (
            <ModalContainer
              width={50}
              fill={theme.colors.black}
              onClickClose={onClickClose}
            >
              <Container {...props}>
                <ContainerText>
                  <Title variant="semiBold">
                    {t('workOrder.confirmChange')}
                  </Title>
                  {/* Des-anular no reactiva nada de fuera del recorrido
                      (2026-08-05). Se dice ANTES de confirmar, porque desde
                      la interfaz parece que «se deshace todo». */}
                  {orderType === OrderType.SurveillanceRoute &&
                    currentState === OrderState.Annulled && (
                      <Typography size="XS">
                        {t('changeState.undoAnnulWarning')}
                      </Typography>
                    )}
                </ContainerText>
                <MainButtonStyled text={t('common:yes')} onClick={sendData} />
                <MainButtonStyled
                  text={t('common:no')}
                  variant="secondary"
                  onClick={() => setInitialModal(!initialModal)}
                />
              </Container>
            </ModalContainer>
          )}
        </>
      )}
    </>
  );
};
